import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, CellStyle } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { getPrompts } from '../../../services/promptsApi';
import type { Prompt } from '../../../services/promptsApi';
import { useErrorDialog } from '../../../hooks/useErrorDialog';
import { ErrorDialog } from '../../../components/common/errorDialog';
import { CustomTextInput } from '../../../components/common/inputFields/customTextInput';
import { formatDateTime } from '../../../utils/formatDate';
import '../../../assets/css/AGGrid-custom.css';
import '../../../assets/css/Text-General.css';

ModuleRegistry.registerModules([AllCommunityModule]);

interface PromptListProps {
  onPromptSelect: (prompt: Prompt) => void;
  refreshTrigger: number;
}

export const PromptList = ({
  onPromptSelect,
  refreshTrigger,
}: PromptListProps) => {
  const gridRef = useRef<AgGridReact>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  useEffect(() => {
    fetchPrompts();
  }, [refreshTrigger]);

  const fetchPrompts = async () => {
    setLoading(true);
    const response = await getPrompts();

    if (response.success && response.data) {
      setPrompts(response.data);
    } else {
      showErrorFromResponse(response);
    }
    setLoading(false);
  };

  const filteredPrompts = useMemo(() => {
    if (!searchQuery) return prompts;

    const query = searchQuery.toLowerCase();
    return prompts.filter(
      (prompt) =>
        prompt.prompt_key.toLowerCase().includes(query) ||
        prompt.prompt_name.toLowerCase().includes(query) ||
        (prompt.description && prompt.description.toLowerCase().includes(query))
    );
  }, [prompts, searchQuery]);


  const columnDefs: ColDef[] = useMemo(() => [
    {
      field: 'prompt_key',
      headerName: 'Prompt Key',
      width: 250,
      cellStyle: { fontFamily: 'monospace', fontWeight: '500' } as CellStyle,
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'prompt_name',
      headerName: 'Name',
      width: 200,
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
      valueGetter: (params) => params.data?.description || '-',
      wrapText: true,
      autoHeight: true,
      cellStyle: { whiteSpace: 'normal', lineHeight: '1.35', alignItems: 'flex-start' } as CellStyle,
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'version',
      headerName: 'Version',
      width: 100,
      valueFormatter: (params) => `v${params.value}`,
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 180,
      valueFormatter: (params) => formatDateTime(params.value),
      headerClass: 'ag-left-aligned-header',
    },
  ], []);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1, p: '5px', alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <CustomTextInput
            label="Search prompts"
            value={searchQuery}
            onChange={(val) => typeof val === 'string' && setSearchQuery(val)}
          />
        </Box>
      </Box>

      {filteredPrompts.length === 0 ? (
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          {searchQuery
            ? 'No prompts match your filters.'
            : 'No prompts found. Click "Create Prompt" to add a new prompt.'}
        </Alert>
      ) : (
        <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}>
          <div className="ag-theme-alpine" style={{ width: '100%', height: '100%', position: 'absolute' }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredPrompts}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              suppressRowTransform
              getRowId={(params) => `${params.data.prompt_key}-${params.data.version}`}
              onRowClicked={(params) => onPromptSelect(params.data)}
              suppressRowClickSelection={false}
            />
          </div>
        </Box>
      )}

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

